import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface CreateListSlideProps {
  visible: boolean
  onCreated: (listId: string, listName: string) => void
  onCancel: () => void
  createList: (name: string) => Promise<{ id: string; name: string } | null>
}

export function CreateListSlide({
  visible,
  onCreated,
  onCancel,
  createList,
}: CreateListSlideProps) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset state when slide is hidden (e.g. popover closed externally)
  useEffect(() => {
    if (!visible) {
      setName('')
      setError(null)
      setIsCreating(false)
    }
  }, [visible])

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) return

    setIsCreating(true)
    setError(null)

    try {
      const result = await createList(trimmed)

      if (result) {
        setName('')
        setError(null)
        onCreated(result.id, result.name)
      } else {
        setError('Failed to create list')
      }
    } finally {
      setIsCreating(false)
    }
  }, [name, createList, onCreated])

  const handleCancel = useCallback(() => {
    setName('')
    setError(null)
    onCancel()
  }, [onCancel])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleCreate()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
    },
    [handleCreate, handleCancel],
  )

  return (
    <div
      className="absolute inset-0 bg-popover transition-transform duration-200 ease-in-out"
      style={{ transform: visible ? 'translateX(0)' : 'translateX(100%)' }}
      onTransitionEnd={() => {
        if (visible) inputRef.current?.focus()
      }}
    >
      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm font-medium">Create a new list</p>
        <Input
          ref={inputRef}
          value={name}
          onChange={(e) => {
            if (e.target.value.length <= 40) {
              setName(e.target.value)
              setError(null)
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder="List name"
          maxLength={40}
          aria-invalid={!!error}
        />
        {error && <p className="text-destructive text-xs">{error}</p>}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
          >
            Create
          </Button>
          <Button size="sm" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
